import React from "react";

// External
import styled from "styled-components";
import { BsChevronDown } from "react-icons/bs";
import { scroller } from "react-scroll";

// Components
import { SectionBorderMagicButton } from "../../components/form/Button";
import TrustedBySection from "../trusted-by-section";
import IconButton from "../../components/form/IconButton";
import SplashText from "../../components/SplashText";
import { Section as PageSection } from "../../components/layout/layout-styles";
import { Heading as HeadingStyles } from "../../components/common/Typography/type-styles";

// Global Variables
import { breakpoints, colors, fontFamily, spacing } from "../../../theme";
import { STATIC_PFP_URL } from "../../utils/constants/projects";
import { name } from "../../../config";

const Section = PageSection;

const ImageWrapper = styled.div`
  text-align: center;
  padding: ${spacing(3)} 0 ${spacing(8)};
`;

const ContentStack = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  gap: ${spacing(8)};
`;

const TextBlock = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  gap: ${spacing(2)};
`;

const HeroAvatar = styled.div`
  margin: 0 auto;
  width: 208px;
  aspect-ratio: 1 / 1;
  overflow: hidden;
  border-radius: 50%;
  background: ${colors.medium};

  @media only screen and (min-width: ${breakpoints.tablet}) {
    width: 318px;
  }

  img {
    display: block;
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const Heading = styled(HeadingStyles)`
  margin: 0 auto;
  @media only screen and (min-width: 420px) {
    font-size: 36px;
  }

  @media only screen and (min-width: 576px) {
    font-size: 44px;
    width: 80%;
  }

  @media only screen and (min-width: ${breakpoints.tablet}) {
    font-size: 52px;
  }

  @media only screen and (min-width: ${breakpoints.desktop}) {
    font-size: 60px;
  }

  @media only screen and (min-width: 1200px) {
    width: 70%;
  }

  @media only screen and (min-width: 1400px) {
    font-size: 66px;
  }

  @media only screen and (min-width: 1510px) {
    font-size: 72px;
    width: 60%;
  }
  @media only screen and (min-width: 1920px) {
    width: 55%;
  }

  @media only screen and (min-width: 2100px) {
    width: 45%;
  }

  @media only screen and (min-width: 2500px) {
    width: 35%;
  }
`;

const HelperText = styled.p`
  font-family: ${fontFamily.medium};
  font-size: 14px;
  width: 60%;
  margin: 0 auto;
  line-height: 140%;
  letter-spacing: 1%;
  text-align: center;

  @media only screen and (min-width: 420px) {
    font-size: 16px;
    width: 80%;
  }

  @media only screen and (min-width: 576px) {
    font-size: 18px;
  }
  @media only screen and (min-width: ${breakpoints.tablet}) {
    font-size: 20px;
    width: 60%;
  }
  @media only screen and (min-width: ${breakpoints.desktop}) {
    font-size: 24px;
    width: 55%;
  }

  @media only screen and (min-width: 1370px) {
    width: 50%;
  }

  @media only screen and (min-width: 1510px) {
    width: 40%;
  }

  @media only screen and (min-width: 1800px) {
    width: 30%;
  }
`;

const TrustedBy = styled.div`
  margin-top: 48px;

  @media only screen and (min-width: ${breakpoints.desktop}) {
    margin-top: 56px;
  }
`;

const ScrollDownButton = styled(IconButton)`
  position: absolute;
  display: none;
  left: 50%;
  transform: translateX(-50%);
  bottom: ${spacing(5)};
`;

const HeroSection = () => {
  const scrollToSkillsSection = () => {
    scroller.scrollTo("mySkills", {
      duration: 700,
      smooth: true,
      offset: -80,
    });
  };

  const scrollToContactMeSection = () => {
    scroller.scrollTo("contactMe", {
      duration: 1000,
      smooth: true,
      offset: -80,
    });
  };

  return (
    <Section $flushTop name="hero" as="section">
      <ImageWrapper>
        <HeroAvatar>
          <img
            src={STATIC_PFP_URL}
            alt={name}
            width={318}
            height={318}
            fetchPriority="high"
            loading="eager"
            decoding="async"
          />
        </HeroAvatar>
      </ImageWrapper>
      <ContentStack>
        <TextBlock>
          <Heading>
            Hello I'm a senior &nbsp;
            <br />
            <SplashText variant="accent">frontend</SplashText>
            &nbsp;developer
          </Heading>
          <HelperText>
            I craft scalable products with modern web technologies.
          </HelperText>
        </TextBlock>
        <SectionBorderMagicButton onClick={scrollToContactMeSection}>
          Contact me
        </SectionBorderMagicButton>
      </ContentStack>
      <TrustedBy>
        <TrustedBySection />
      </TrustedBy>
      <ScrollDownButton
        bgColor={colors.black}
        iconColor={colors.black}
        onClick={scrollToSkillsSection}
        ariaLabel="scroll to skills section"
      >
        <BsChevronDown />
      </ScrollDownButton>
    </Section>
  );
};

export default HeroSection;
